import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react'
import { V, serif, mono, eyebrow, card, btnGold, btnGhost } from '../theme'
import StrategyInfoTooltip from './StrategyInfoTooltip'

const TB_STOCKS = [
  { ticker: 'AAPL', name: 'Apple Inc.',      price: 187, ivRank: 28, trend: 'Bullish',  liquidity: 'High',   notes: 'Strong support at $182. Earnings in 6 weeks.' },
  { ticker: 'MSFT', name: 'Microsoft Corp.', price: 415, ivRank: 22, trend: 'Bullish',  liquidity: 'High',   notes: 'Near highs. Low IV — premium thin.' },
  { ticker: 'NVDA', name: 'NVIDIA Corp.',    price: 875, ivRank: 68, trend: 'Volatile', liquidity: 'High',   notes: 'High IV. Earnings next week — avoid.' },
  { ticker: 'KO',   name: 'Coca-Cola Co.',   price: 61,  ivRank: 18, trend: 'Neutral',  liquidity: 'Medium', notes: 'Slow mover. Good Wheel candidate.' },
  { ticker: 'F',    name: 'Ford Motor Co.',  price: 12,  ivRank: 45, trend: 'Bearish',  liquidity: 'Medium', notes: 'Too cheap for good premium.' },
];
const TB_STRATEGIES = [
  { id: 'csp',    label: 'Cash-Secured Put',  best: ['Bullish','Neutral'],  desc: 'Sell a put. Get paid to potentially buy shares at a discount.' },
  { id: 'cc',     label: 'Covered Call',      best: ['Neutral','Bullish'],  desc: 'Sell a call on shares you own. Generate income each expiration.' },
  { id: 'wheel',  label: 'The Wheel',         best: ['Bullish','Neutral'],  desc: 'CSP → assigned → Covered Call → repeat.' },
  { id: 'spread', label: '0DTE Credit Spread',best: ['Volatile'],           desc: 'Defined-risk day trade. SPX only.' },
];
function getStrikes(stock, strategy) {
  const p = stock.price;
  return strategy === 'spread' ? [
    { strike: Math.round(p - p*0.01), delta: -0.18, premium: (p*0.002).toFixed(2), dte: 0, label: 'Put spread (18Δ) ★' },
    { strike: Math.round(p + p*0.01), delta: 0.18,  premium: (p*0.002).toFixed(2), dte: 0, label: 'Call spread (18Δ)' },
  ] : [
    { strike: Math.round(p * (strategy === 'cc' ? 1.05 : 0.95)), delta: strategy === 'cc' ? 0.16 : -0.16, premium: (p*0.004).toFixed(2), dte: 30, label: 'Beginner (16Δ) ★' },
    { strike: Math.round(p * (strategy === 'cc' ? 1.03 : 0.97)), delta: strategy === 'cc' ? 0.28 : -0.28, premium: (p*0.007).toFixed(2), dte: 30, label: 'Intermediate (28Δ)' },
    { strike: Math.round(p * (strategy === 'cc' ? 1.01 : 0.99)), delta: strategy === 'cc' ? 0.40 : -0.40, premium: (p*0.011).toFixed(2), dte: 30, label: 'Aggressive (40Δ)' },
  ];
}
function scoreTrade({ stock, strategy, strike }) {
  let score = 0; let feedback = [];
  if (stock.ivRank >= 30 && stock.ivRank < 60) { score += 25; feedback.push({ good: true, text: `IV Rank ${stock.ivRank} — solid premium environment.` }); }
  else if (stock.ivRank < 30) { score += 10; feedback.push({ good: false, text: `IV Rank ${stock.ivRank} — premium is thin. Wait for higher IV.` }); }
  else { score += 5; feedback.push({ good: false, text: `IV Rank ${stock.ivRank} — very high. Event risk likely present.` }); }
  const strat = TB_STRATEGIES.find(s => s.id === strategy);
  if (strat?.best.includes(stock.trend)) { score += 25; feedback.push({ good: true, text: `${strat.label} fits a ${stock.trend} outlook.` }); }
  else { score += 5; feedback.push({ good: false, text: `${strat.label} is not ideal for a ${stock.trend} stock.` }); }
  const d = Math.abs(strike.delta);
  if (d >= 0.12 && d <= 0.18) { score += 25; feedback.push({ good: true, text: `Delta ${d.toFixed(2)} — beginner sweet spot. ~85% probability of expiring worthless.` }); }
  else if (d > 0.18 && d <= 0.28) { score += 18; feedback.push({ good: true, text: `Delta ${d.toFixed(2)} — intermediate range. More premium, moderate assignment risk.` }); }
  else if (d < 0.12) { score += 12; feedback.push({ good: true, text: `Delta ${d.toFixed(2)} — very conservative. Very high probability but minimal premium.` }); }
  else { score += 5; feedback.push({ good: false, text: `Delta ${d.toFixed(2)} — aggressive. Higher assignment risk. Start closer to 0.15.` }); }
  if (stock.liquidity === 'High') { score += 15; feedback.push({ good: true, text: `${stock.ticker} has high liquidity.` }); }
  else { score += 8; feedback.push({ good: true, text: `${stock.ticker} has ${stock.liquidity.toLowerCase()} liquidity.` }); }
  if (stock.price >= 20) { score += 10; feedback.push({ good: true, text: `Price $${stock.price} — above $20 minimum.` }); }
  else { score += 0; feedback.push({ good: false, text: `Price $${stock.price} — too cheap for meaningful premium.` }); }
  return { score: Math.min(score, 100), feedback };
}

// ── Inner Work connection helpers ─────────────────────────────
const MINI_EMOTIONS = [
  { key: 'calm',       emoji: '😌', label: 'Calm',        prompt: "What has created this steadiness today?" },
  { key: 'focused',    emoji: '🎯', label: 'Focused',     prompt: "What is clearest to you about this trade right now?" },
  { key: 'anxious',    emoji: '😰', label: 'Anxious',     prompt: "Where do you feel that in your body? Is this about the trade — or did it arrive with you?" },
  { key: 'rushed',     emoji: '⚡', label: 'Rushed',      prompt: "What would happen if you waited ten more minutes?" },
  { key: 'frustrated', emoji: '😤', label: 'Frustrated',  prompt: "Is this frustration about this trade — or did it come from somewhere else today?" },
  { key: 'confident',  emoji: '💪', label: 'Confident',   prompt: "Is this confidence grounded and clear — or closer to excitement?" },
  { key: 'numb',       emoji: '🌫️', label: 'Numb',        prompt: "What might you be protecting yourself from right now?" },
  { key: 'excited',    emoji: '🤑', label: 'Excited',     prompt: "Is there a plan supporting this excitement — or is the excitement the plan?" },
];

function shouldShowCheckin(state) {
  const setting = (state && state.innerWorkPrompts) || 'always';
  if (setting === 'never') return false;
  if (setting === 'always') return true;
  const today = new Date().toLocaleDateString();
  const checkins = (state && state.innerWorkCheckins) || [];
  return !checkins.some(c => {
    const d = c.date || (c.timestamp ? new Date(c.timestamp).toLocaleDateString() : null);
    return d === today;
  });
}

function MiniCheckin({ title, subtitle, confirmLabel, skipLabel, tradeDetails, onCheckin, onSkip }) {
  const [emotion, setEmotion] = useState(null);
  const [note, setNote] = useState('');
  const selected = MINI_EMOTIONS.find(e => e.key === emotion);
  const ta = { width: '100%', padding: '10px 12px', background: V.bg3, border: `0.5px solid ${V.border}`, borderRadius: '7px', color: V.text, fontSize: '13px', outline: 'none', resize: 'vertical', minHeight: '70px', fontFamily: "'Jost', sans-serif", lineHeight: 1.6, marginTop: '10px' };
  return (
    <div style={{ background: V.bg2, border: `0.5px solid rgba(201,168,76,0.3)`, borderLeft: `3px solid ${V.gold}`, borderRadius: '10px', padding: '20px 22px', marginTop: '16px' }}>
      <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '18px', fontWeight: 400, color: V.goldL, marginBottom: '6px' }}>{title || 'Before you enter this trade'}</h3>
      <p style={{ fontSize: '13px', color: V.muted, lineHeight: 1.7, marginBottom: '16px' }}>{subtitle || 'Take 15 seconds. How are you arriving at this moment?'}</p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '14px' }}>
        {MINI_EMOTIONS.map(e => (
          <button key={e.key} onClick={() => setEmotion(emotion === e.key ? null : e.key)} style={{ padding: '8px 12px', background: emotion === e.key ? 'rgba(201,168,76,0.12)' : V.bg3, border: `0.5px solid ${emotion === e.key ? V.gold : V.border}`, borderRadius: '8px', cursor: 'pointer', color: emotion === e.key ? V.goldL : V.muted, fontSize: '13px', display: 'flex', gap: '5px', alignItems: 'center', transition: 'all 0.15s' }}>
            <span>{e.emoji}</span><span style={{ fontFamily: "'Jost', sans-serif" }}>{e.label}</span>
          </button>
        ))}
      </div>
      {selected && (
        <div style={{ marginBottom: '12px' }}>
          <p style={{ fontSize: '13px', color: V.text, lineHeight: 1.7, fontFamily: "'Jost', sans-serif" }}>{selected.prompt}</p>
          <textarea style={ta} placeholder="Your thoughts (optional — this is only for you)" value={note} onChange={e => setNote(e.target.value)} />
        </div>
      )}
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '14px' }}>
        <button style={{ ...btnGold, padding: '10px 20px', fontSize: '13px' }} onClick={() => onCheckin({ emotion, note, tradeDetails })}>
          {confirmLabel || 'I\'ve checked in — submit my trade →'}
        </button>
        <button style={{ ...btnGhost, padding: '10px 18px', fontSize: '13px' }} onClick={onSkip}>
          {skipLabel || 'Skip and submit →'}
        </button>
      </div>
    </div>
  );
}


function TradeBuilderWizard({ onTradeComplete, onBack, state, onUpdate }) {
  const [step, setStep]         = useState(0);
  const [stock, setStock]       = useState(null);
  const [trend, setTrend]       = useState(null);
  const [strategy, setStrategy] = useState(null);
  const [strike, setStrike]     = useState(null);
  const [result, setResult]     = useState(null);
  const STEPS = ['Choose Stock', 'Read Chart', 'Strategy', 'Strike', 'Score'];

  const [pendingFinish, setPendingFinish] = useState(false);
  const finish = () => {
    const r = scoreTrade({ stock, strategy, strike });
    setResult(r);
    setStep(4);
    setPendingFinish(false);
    onTradeComplete && onTradeComplete(r.score);
  };
  const tryFinish = () => {
    if (shouldShowCheckin(state)) { setPendingFinish(true); } else { finish(); }
  };
  const handleTBCheckin = ({ emotion, note }) => {
    if (onUpdate && emotion) {
      const entry = { timestamp: Date.now(), date: new Date().toLocaleDateString(), type: 'pre_trade', emotion, note, tradeDetails: { ticker: stock?.ticker, strategy } };
      onUpdate({ innerWorkCheckins: [...((state && state.innerWorkCheckins) || []), entry] });
    }
    finish();
  };
  const reset = () => { setStep(0); setStock(null); setTrend(null); setStrategy(null); setStrike(null); setResult(null); setPendingFinish(false); };

  const selCard = (selected) => ({ background: selected ? 'rgba(201,168,76,0.08)' : V.bg2, border: `0.5px solid ${selected ? 'rgba(201,168,76,0.45)' : V.border}`, borderRadius: '10px', padding: '14px 16px', marginBottom: '8px', cursor: 'pointer' });

  return (
    <div style={{ padding: '28px 40px', maxWidth: '860px', width: '100%', margin: '0 auto', boxSizing: 'border-box' }}>
      <button style={{ ...btnGhost, marginBottom: '20px', fontSize: '12px' }} onClick={onBack}>← Dashboard</button>
      <div style={{ ...eyebrow, marginBottom: '6px' }}>♀ Practice Tool</div>
      <h2 style={{ ...serif, fontSize: '28px', fontWeight: 500, margin: '0 0 4px' }}>Trade Builder Wizard</h2>
      <p style={{ fontSize: '13px', color: V.muted, marginBottom: '20px' }}>Build a paper trade step-by-step. Get scored on every decision.</p>

      {/* Step bar */}
      <div style={{ display: 'flex', gap: '6px', marginBottom: '24px' }}>
        {STEPS.map((s, i) => (
          <div key={i} style={{ flex: 1 }}>
            <div style={{ height: '3px', borderRadius: '2px', marginBottom: '5px', background: i <= step ? V.gold : 'rgba(240,237,230,0.08)' }} />
            <div style={{ fontSize: '9px', letterSpacing: '0.06em', color: i === step ? V.goldL : V.dim, textAlign: 'center' }}>{s.toUpperCase()}</div>
          </div>
        ))}
      </div>

      {step === 0 && (
        <div>
          <p style={{ fontSize: '13px', color: V.muted, marginBottom: '16px' }}>Evaluate each candidate. Consider IV Rank, trend, liquidity, and price.</p>
          {TB_STOCKS.map(s => (
            <div key={s.ticker} style={selCard(stock?.ticker === s.ticker)} onClick={() => setStock(s)}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ ...mono, fontSize: '17px', color: V.goldL, fontWeight: 500 }}>{s.ticker}</span>
                <span style={{ ...mono, fontSize: '15px' }}>${s.price}</span>
              </div>
              <div style={{ display: 'flex', gap: '16px', marginTop: '8px', fontSize: '12px' }}>
                <span>IV Rank: <strong style={{ color: s.ivRank >= 30 ? V.sage : V.muted }}>{s.ivRank}</strong></span>
                <span>Trend: <strong style={{ color: s.trend === 'Bullish' ? V.sage : s.trend === 'Bearish' ? V.rose : V.sky }}>{s.trend}</strong></span>
                <span>Liq: <strong style={{ color: s.liquidity === 'High' ? V.sage : V.muted }}>{s.liquidity}</strong></span>
              </div>
              <div style={{ fontSize: '11px', color: V.muted, marginTop: '6px', fontStyle: 'italic' }}>{s.notes}</div>
            </div>
          ))}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px' }}>
            <button style={{ ...btnGold, opacity: stock ? 1 : 0.35, cursor: stock ? 'pointer' : 'not-allowed' }} onClick={() => stock && setStep(1)}>Next →</button>
          </div>
        </div>
      )}

      {step === 1 && stock && (
        <div>
          <p style={{ fontSize: '13px', color: V.muted, marginBottom: '16px' }}>Based on {stock.ticker} at ${stock.price} — what's your directional read?</p>
          <svg viewBox="0 0 500 80" style={{ width: '100%', height: '70px', marginBottom: '20px', background: V.bg2, borderRadius: '8px', padding: '8px' }}>
            {stock.trend === 'Bullish'  && <polyline points="0,65 120,50 240,38 360,25 500,12" fill="none" stroke={V.sage} strokeWidth="2"/>}
            {stock.trend === 'Bearish'  && <polyline points="0,12 120,25 240,42 360,58 500,70" fill="none" stroke={V.rose} strokeWidth="2"/>}
            {stock.trend === 'Neutral'  && <polyline points="0,40 100,35 200,45 300,36 400,44 500,40" fill="none" stroke={V.sky} strokeWidth="2"/>}
            {stock.trend === 'Volatile' && <polyline points="0,40 80,12 160,62 240,18 320,65 400,20 500,50" fill="none" stroke={V.gold} strokeWidth="2"/>}
          </svg>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '20px' }}>
            {['Bullish — sideways to up', 'Bearish — expecting down', 'Neutral — no strong opinion', "Too risky — I'd skip this"].map(t => (
              <div key={t} style={selCard(trend === t)} onClick={() => setTrend(t)}>
                <div style={{ fontSize: '13px', fontWeight: 500 }}>{t.split('—')[0].trim()}</div>
                <div style={{ fontSize: '11px', color: V.muted, marginTop: '2px' }}>— {t.split('—')[1]?.trim()}</div>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <button style={btnGhost} onClick={() => setStep(0)}>← Back</button>
            <button style={{ ...btnGold, opacity: trend ? 1 : 0.35, cursor: trend ? 'pointer' : 'not-allowed' }} onClick={() => trend && setStep(2)}>Next →</button>
          </div>
        </div>
      )}

      {step === 2 && stock && (
        <div>
          <p style={{ fontSize: '13px', color: V.muted, marginBottom: '16px' }}>{stock.ticker} is {stock.trend.toLowerCase()} with IV Rank {stock.ivRank}.</p>
          {TB_STRATEGIES.map(s => {
            const fit = s.best.includes(stock.trend);
            return (
              <div key={s.id} style={selCard(strategy === s.id)} onClick={() => setStrategy(s.id)}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ ...mono, fontSize: '12px', color: V.goldL }}>{s.id.toUpperCase()}</span>
                  <span style={{ fontSize: '14px', fontWeight: 500 }}>{s.label}</span>
                  {fit && <span style={{ fontSize: '10px', background: 'rgba(122,171,138,0.12)', color: V.sage, padding: '2px 8px', borderRadius: '4px' }}>GOOD FIT</span>}
                </div>
                <p style={{ fontSize: '12px', color: V.muted, marginTop: '5px' }}>{s.desc}</p>
              </div>
            );
          })}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px' }}>
            <button style={btnGhost} onClick={() => setStep(1)}>← Back</button>
            <button style={{ ...btnGold, opacity: strategy ? 1 : 0.35, cursor: strategy ? 'pointer' : 'not-allowed' }} onClick={() => strategy && setStep(3)}>Next →</button>
          </div>
        </div>
      )}

      {step === 3 && stock && strategy && (
        <div>
          <p style={{ fontSize: '13px', color: V.sky, marginBottom: '16px' }}>💡 Beginners: target the ~0.15 delta strike — approximately 85% probability of expiring worthless.</p>
          <div style={{ background: V.bg2, border: `0.5px solid ${V.border}`, borderRadius: '10px', overflow: 'hidden', marginBottom: '16px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: '8px', padding: '10px 14px', background: V.bg3, fontSize: '9px', color: V.dim, letterSpacing: '0.08em' }}>
              <span>STRIKE</span><span style={{ textAlign: 'right' }}>DELTA</span><span style={{ textAlign: 'right' }}>PREMIUM</span><span style={{ textAlign: 'right' }}>CREDIT</span>
            </div>
            {getStrikes(stock, strategy).map((sk, i) => {
              const sweet = Math.abs(sk.delta) >= 0.12 && Math.abs(sk.delta) <= 0.18;
              return (
                <div key={i} style={{ ...selCard(strike?.strike === sk.strike), borderRadius: 0, borderLeft: 'none', borderRight: 'none', borderTop: 'none', marginBottom: 0, display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: '8px', alignItems: 'center' }}
                  onClick={() => setStrike(sk)}>
                  <span><span style={{ ...mono, fontSize: '15px', fontWeight: 500 }}>${sk.strike}</span> <span style={{ fontSize: '10px', color: sweet ? V.gold : V.muted }}>{sk.label}</span></span>
                  <span style={{ ...mono, fontSize: '13px', textAlign: 'right', color: sweet ? V.goldL : V.text }}>{Math.abs(sk.delta).toFixed(2)}</span>
                  <span style={{ ...mono, fontSize: '13px', textAlign: 'right', color: V.sage }}>${sk.premium}</span>
                  <span style={{ ...mono, fontSize: '13px', textAlign: 'right', color: V.sage }}>${(sk.premium * 100).toFixed(0)}</span>
                </div>
              );
            })}
          </div>
          {!pendingFinish && (
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <button style={btnGhost} onClick={() => setStep(2)}>← Back</button>
              <button style={{ ...btnGold, opacity: strike ? 1 : 0.35, cursor: strike ? 'pointer' : 'not-allowed' }} onClick={() => strike && tryFinish()}>Score My Trade →</button>
            </div>
          )}
          {pendingFinish && (
            <MiniCheckin
              tradeDetails={{ ticker: stock?.ticker, strategy }}
              confirmLabel="I've checked in — score my trade →"
              skipLabel="Skip and score →"
              onCheckin={handleTBCheckin}
              onSkip={finish}
            />
          )}
        </div>
      )}

      {step === 4 && result && (
        <div>
          <div style={{ textAlign: 'center', marginBottom: '24px' }}>
            <div style={{ ...mono, fontSize: '56px', fontWeight: 500, color: result.score >= 80 ? V.sage : result.score >= 60 ? V.goldL : V.rose, lineHeight: 1 }}>{result.score}</div>
            <div style={{ fontSize: '13px', color: V.muted, marginTop: '6px' }}>Trade Score · +{result.score >= 80 ? 150 : result.score >= 60 ? 80 : 40} XP</div>
          </div>
          <div style={card}>
            {result.feedback.map((f, i) => (
              <div key={i} style={{ display: 'flex', gap: '10px', marginBottom: '10px', alignItems: 'flex-start' }}>
                <span style={{ color: f.good ? V.sage : V.rose, flexShrink: 0 }}>{f.good ? '✓' : '✗'}</span>
                <span style={{ fontSize: '13px', color: V.muted, lineHeight: 1.5 }}>{f.text}</span>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
            <button style={btnGold} onClick={reset}>Build Another Trade →</button>
            <button style={btnGhost} onClick={onBack}>Dashboard</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// SECTION 6 — P&L TRACKER
// ═══════════════════════════════════════════════════════════════

export { MiniCheckin, shouldShowCheckin }
export default TradeBuilderWizard
